<template>
  <div class="flex flex-col h-full gap-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">AI智能体助手</h1>
        <p class="text-sm text-slate-500 mt-1">基于RAG技术的项目管理智能辅助，支持智能问数、材料审核、专家匹配、报告生成</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-emerald-100 text-emerald-600 rounded-full text-xs font-semibold flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          智能体在线
        </span>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 grid grid-cols-3 gap-5 min-h-0">
      <!-- Left: AI Agent Selection -->
      <div class="glass-card rounded-2xl p-5 flex flex-col">
        <h3 class="text-base font-bold text-slate-800 mb-4">选择智能体</h3>
        <div class="flex-1 space-y-3 overflow-y-auto">
          <button 
            v-for="agent in agents" 
            :key="agent.id"
            class="w-full p-4 rounded-xl border-2 text-left transition-all"
            :class="selectedAgent === agent.id ? 'border-purple-500 bg-purple-50' : 'border-slate-100 hover:border-purple-200 bg-white'"
            @click="selectedAgent = agent.id"
          >
            <div class="flex items-start gap-3">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                   :class="agent.color">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="agent.icon"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-slate-800">{{ agent.name }}</span>
                  <span v-if="agent.isNew" class="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">NEW</span>
                </div>
                <p class="text-xs text-slate-500 mt-1">{{ agent.description }}</p>
                <div class="flex items-center gap-2 mt-2">
                  <span class="text-xs text-slate-400">已使用 {{ agent.usageCount }} 次</span>
                  <span class="w-1 h-1 rounded-full bg-slate-300"></span>
                  <span class="text-xs text-emerald-600">准确率 {{ agent.accuracy }}%</span>
                </div>
              </div>
            </div>
          </button>
        </div>

        <!-- Quick Actions -->
        <div class="mt-4 pt-4 border-t border-slate-100">
          <h4 class="text-xs font-semibold text-slate-500 mb-3 uppercase">快捷操作</h4>
          <div class="grid grid-cols-2 gap-2">
            <button class="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-xs text-slate-600 text-left transition-colors">
              📊 生成月度报告
            </button>
            <button class="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-xs text-slate-600 text-left transition-colors">
              🔍 批量审核材料
            </button>
            <button class="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-xs text-slate-600 text-left transition-colors">
              👥 专家智能推荐
            </button>
            <button class="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-xs text-slate-600 text-left transition-colors">
              📈 项目风险分析
            </button>
          </div>
        </div>
      </div>

      <!-- Center: Chat Interface -->
      <div class="col-span-2 glass-card rounded-2xl flex flex-col overflow-hidden">
        <!-- Chat Header -->
        <div class="p-4 border-b border-slate-100 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
            </div>
            <div>
              <div class="font-semibold text-slate-800">{{ currentAgent.name }}</div>
              <div class="text-xs text-slate-500">{{ currentAgent.description }}</div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button class="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="清空对话">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
            </button>
            <button class="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="导出对话">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Chat Messages -->
        <div class="flex-1 overflow-y-auto p-4 space-y-4" ref="chatContainer">
          <!-- Welcome Message -->
          <div v-if="messages.length === 0" class="text-center py-8">
            <div class="w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="currentAgent.icon"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">欢迎使用 {{ currentAgent.name }}</h3>
            <p class="text-sm text-slate-500 mb-6">{{ currentAgent.welcomeMessage }}</p>
            <div class="flex flex-wrap justify-center gap-2">
              <button 
                v-for="suggestion in currentAgent.suggestions" 
                :key="suggestion"
                class="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-purple-100 text-slate-600 hover:text-purple-600 text-xs transition-colors"
                @click="sendMessage(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>

          <!-- Message List -->
          <template v-else>
            <div v-for="(message, index) in messages" :key="index" class="flex gap-3" :class="message.type === 'user' ? 'flex-row-reverse' : ''">
              <!-- Avatar -->
              <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                   :class="message.type === 'user' ? 'bg-blue-500' : 'bg-gradient-to-br from-purple-500 to-pink-500'">
                <svg v-if="message.type === 'user'" class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                <svg v-else class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="currentAgent.icon"/>
                </svg>
              </div>

              <!-- Message Content -->
              <div class="max-w-[80%]" :class="message.type === 'user' ? 'text-right' : ''">
                <div class="inline-block px-4 py-2.5 rounded-2xl text-sm"
                     :class="message.type === 'user' ? 'bg-blue-500 text-white rounded-br-md' : 'bg-slate-100 text-slate-700 rounded-bl-md'">
                  {{ message.content }}
                </div>
                <div class="text-xs text-slate-400 mt-1">{{ message.time }}</div>
              </div>
            </div>

            <!-- AI Thinking -->
            <div v-if="isThinking" class="flex gap-3">
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="currentAgent.icon"/>
                </svg>
              </div>
              <div class="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div class="flex gap-1">
                  <span class="w-2 h-2 rounded-full bg-slate-400 animate-bounce"></span>
                  <span class="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style="animation-delay: 0.1s"></span>
                  <span class="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style="animation-delay: 0.2s"></span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Input Area -->
        <div class="p-4 border-t border-slate-100">
          <div class="flex items-end gap-3">
            <div class="flex-1 relative">
              <textarea 
                v-model="inputMessage"
                rows="1"
                class="w-full pl-4 pr-12 py-3 rounded-xl border border-slate-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all text-sm resize-none"
                placeholder="输入您的问题，或上传文件进行分析..."
                @keydown.enter.prevent="sendMessage(inputMessage)"
                @input="autoResize"
                ref="inputArea"
              ></textarea>
              <button class="absolute right-3 bottom-3 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="上传文件">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
                </svg>
              </button>
            </div>
            <button 
              class="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all flex items-center gap-2"
              :disabled="!inputMessage.trim() || isThinking"
              @click="sendMessage(inputMessage)"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
              发送
            </button>
          </div>
          <div class="flex items-center justify-between mt-2">
            <div class="flex items-center gap-2">
              <span class="text-xs text-slate-400">基于</span>
              <span class="text-xs font-medium text-purple-600">RAG技术</span>
              <span class="text-xs text-slate-400">·</span>
              <span class="text-xs font-medium text-blue-600">国产大模型</span>
            </div>
            <span class="text-xs text-slate-400">按 Enter 发送，Shift + Enter 换行</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIAssistant',
  data() {
    return {
      selectedAgent: this.$route.query.agent || 'qa',
      inputMessage: '',
      isThinking: false,
      messages: [],
      agents: [
        {
          id: 'qa',
          name: '智能问数助手',
          description: '通过自然语言查询项目数据、生成统计报告',
          icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
          color: 'bg-purple-500',
          usageCount: 1256,
          accuracy: 96,
          isNew: false,
          welcomeMessage: '我是智能问数助手，可以通过自然语言帮您查询项目数据、统计分析结果。请问您想了解什么信息？',
          suggestions: [
            '查询2025年申报项目数量',
            '高熵催化领域项目统计',
            '生成本月项目进度报告',
            '对比各学院项目立项率'
          ]
        },
        {
          id: 'review',
          name: '材料审核助手',
          description: '自动审核申报书、任务书、验收报告等材料',
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          color: 'bg-blue-500',
          usageCount: 892,
          accuracy: 94,
          isNew: false,
          welcomeMessage: '我是材料审核助手，可以帮您自动审核申报书、任务书、验收报告等材料，检测完整性、规范性和一致性问题。',
          suggestions: [
            '审核申报书完整性',
            '检查任务书指标一致性',
            '验收报告成果核查',
            '预算合理性分析'
          ]
        },
        {
          id: 'expert',
          name: '专家匹配助手',
          description: '智能推荐评审专家，识别关联关系',
          icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
          color: 'bg-emerald-500',
          usageCount: 567,
          accuracy: 92,
          isNew: false,
          welcomeMessage: '我是专家匹配助手，可以根据项目研究方向智能推荐合适的评审专家，并识别可能的关联关系。',
          suggestions: [
            '推荐高熵催化领域专家',
            '查找新能源电池评审专家',
            '识别专家关联关系',
            '生成专家评审记录分析'
          ]
        },
        {
          id: 'report',
          name: '智能报告助手',
          description: '自动生成项目分析报告、决策建议',
          icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          color: 'bg-amber-500',
          usageCount: 423,
          accuracy: 95,
          isNew: true,
          welcomeMessage: '我是智能报告助手，可以自动生成项目分析报告、趋势预测和决策建议，支持多种报告模板。',
          suggestions: [
            '生成项目态势分析报告',
            '制作经费执行分析图表',
            '输出领域发展趋势预测',
            '生成决策建议书'
          ]
        }
      ]
    }
  },
  computed: {
    currentAgent() {
      return this.agents.find(a => a.id === this.selectedAgent) || this.agents[0]
    }
  },
  methods: {
    autoResize() {
      const textarea = this.$refs.inputArea
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    },
    async sendMessage(content) {
      if (!content.trim() || this.isThinking) return
      
      // Add user message
      this.messages.push({
        type: 'user',
        content: content,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      })
      
      this.inputMessage = ''
      this.isThinking = true
      
      // Reset textarea height
      this.$nextTick(() => {
        const textarea = this.$refs.inputArea
        textarea.style.height = 'auto'
        this.scrollToBottom()
      })
      
      // Simulate AI response
      setTimeout(() => {
        const responses = {
          qa: [
            '根据系统数据，2025年截至目前共申报项目1,580个，其中已立项1,247个，立项率为78.9%。相比去年同期增长15%。',
            '高熵催化领域在2025年申报项目共156个，其中自然科学基金项目89个，重点研发计划42个。该领域申报量同比增长35%，建议关注。',
            '本月项目进度整体良好，856个在研项目中，按计划推进的项目占比87%，存在进度风险的项目有23个，需要重点关注。'
          ],
          review: [
            '已完成对申报书《高熵催化体系的设计研究》的审核，发现以下问题：\n1. 预算明细中设备费占比偏高（45%），建议调整\n2. 研究内容第3点描述不够具体\n3. 附件缺少伦理审查意见\n总体评分：4.2/5',
            '任务书审核完成：与申报书对比，发现考核指标中"发表SCI论文3篇"在任务书中改为"2篇"，存在指标下调问题，建议核实。',
            '验收报告审核结果：项目2024MAT015的验收报告与任务书对比，5项考核指标中4项已达成，1项部分达成。成果清单与任务书要求基本匹配。'
          ],
          expert: [
            '根据项目《高熵催化体系的设计研究》的研究方向，为您推荐以下评审专家：\n1. 张明教授（清华大学）- 高熵材料专家\n2. 李华研究员（中科院）- 催化化学专家\n3. 王强教授（北京大学）- 能源材料专家\n\n⚠️ 注意：李华研究员与项目负责人有合作关系，建议回避。',
            '新能源电池领域专家推荐：\n1. 陈院士（宁德时代首席科学家）\n2. 刘教授（浙江大学）\n3. 赵研究员（中科院物理所）\n\n以上专家均无利益关联，可参与评审。'
          ],
          report: [
            '已为您生成《2025年第一季度项目态势分析报告》，包含以下内容：\n- 项目申报与立项情况\n- 各领域分布分析\n- 经费执行情况\n- 风险预警与建议\n\n报告已保存至文档库，可点击下载。',
            '经费执行分析：在研经费3.2亿元中，已执行1.37亿元（42.8%），执行进度正常。建议关注执行率低于30%的12个项目。'
          ]
        }
        
        const agentResponses = responses[this.selectedAgent] || responses.qa
        const randomResponse = agentResponses[Math.floor(Math.random() * agentResponses.length)]
        
        this.messages.push({
          type: 'ai',
          content: randomResponse,
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        })
        
        this.isThinking = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }, 1500 + Math.random() * 1000)
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.animate-bounce {
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
